import React, { ReactNode } from 'react';
import {
    AdvicesComponent,
    CareCanvasComponent,
    DentalExaminationsComponent,
    DentalProceduresComponent,
    DiagnosisComponent,
    DynamicFormComponent,
    ExaminationsComponent,
    FollowUpComponent,
    InjectionTableComponent,
    LabTestsComponents,
    LabVitalsComponents,
    MedTableComponent,
    MedicalHistoryComponent,
    OpSummaryComponent,
    PPNotesComponent,
    ProceduresComponent,
    ReferComponent,
    SummaryEmptyView,
    SymptomsComponenent,
    VitalsComponent,
} from './prescriptionPad/PadComponents';
import { GeniePadElementsSettingItem } from '../api/pushGenie';
import { useSelector } from 'react-redux';
import { getPxElementConfig, getSectionNameConfig } from '../store/selectors/session';
import { isPrescriptionEmpty } from '../store/selectors/prescriptionPad';
import { PadElements, Prescription, ToolWithMH } from '../store/types/prescription';
import { Visit } from '../store/types/visit';
import { NoPx } from './common/NoPX';
import ViewSummary from './common/ViewSummary';
import { getSectionNameFromKey } from '../pages/utils';
import { rxKeyToHeadingMap, rxKeyToIconMap } from '../pages/opUtils';

const PAD_COMPONENTS: { [key in keyof PadElements]: (props: Partial<ToolWithMH>) => ReactNode } = {
    vitals: (props: { vitals?: PadElements['vitals'] }) => <VitalsComponent {...props} />,
    medicalHistory: (props: { medicalHistory?: PadElements['medicalHistory'] }) => (
        <MedicalHistoryComponent {...props} />
    ),
    symptoms: (props: { symptoms?: PadElements['symptoms'] }) => <SymptomsComponenent {...props} />,
    diagnosis: (props: { diagnosis?: PadElements['diagnosis'] }) => (
        <DiagnosisComponent {...props} />
    ),
    injections: (props: { injections?: PadElements['injections'] }) => (
        <InjectionTableComponent {...props} />
    ),
    medications: (props: { medications?: PadElements['medications'] }) => (
        <MedTableComponent {...props} />
    ),
    labTests: (props: { labTests?: PadElements['labTests'] }) => <LabTestsComponents {...props} />,
    labVitals: (props: { labVitals?: PadElements['labVitals'] }) => (
        <LabVitalsComponents {...props} />
    ),
    examinations: (props: { examinations?: PadElements['examinations'] }) => (
        <ExaminationsComponent {...props} />
    ),
    prescriptionNotes: (props: { prescriptionNotes?: PadElements['prescriptionNotes'] }) => (
        <PPNotesComponent {...props} />
    ),
    refer: (props: { refer?: PadElements['refer'] }) => <ReferComponent {...props} />,
    advices: (props: { advices?: PadElements['advices'] }) => <AdvicesComponent {...props} />,
    followup: (props: { followup?: PadElements['followup'] }) => <FollowUpComponent {...props} />,
    dentalExaminations: (props: { dentalExaminations?: PadElements['dentalExaminations'] }) => {
        return <DentalExaminationsComponent {...props} />;
    },
    dentalProcedures: (props: { dentalProcedures?: PadElements['dentalProcedures'] }) => {
        return <DentalProceduresComponent {...props} />;
    },
    procedures: (props: { procedures?: PadElements['procedures'] }) => {
        return <ProceduresComponent {...props} />;
    },
    opVision: (props: { opVision?: PadElements['opVision'] }) => {
        return (
            <OpSummaryComponent
                // TODO: use the SummaryView component
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                elements={props.opVision}
                heading={rxKeyToHeadingMap['opVision']}
                icon={rxKeyToIconMap['opVision']}
            />
        );
    },
    opIop: (props: { opIop?: PadElements['opIop'] }) => {
        return (
            <OpSummaryComponent
                // TODO: use the SummaryView component
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                elements={props.opIop}
                heading={rxKeyToHeadingMap['opIop']}
                icon={rxKeyToIconMap['opIop']}
            />
        );
    },
    opCurrentSpec: (props: { opIop?: PadElements['opCurrentSpec'] }) => {
        return (
            <OpSummaryComponent
                // TODO: use the SummaryView component
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                elements={props.opCurrentSpec}
                heading={rxKeyToHeadingMap['opCurrentSpec']}
                icon={rxKeyToIconMap['opCurrentSpec']}
            />
        );
    },
    opSubjectiveRefraction: (props: { opIop?: PadElements['opSubjectiveRefraction'] }) => {
        return (
            <OpSummaryComponent
                // TODO: use the SummaryView component
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                elements={props.opSubjectiveRefraction}
                heading={rxKeyToHeadingMap['opSubjectiveRefraction']}
                icon={rxKeyToIconMap['opSubjectiveRefraction']}
            />
        );
    },
    opAutoRefraction: (props: { opIop?: PadElements['opAutoRefraction'] }) => {
        return (
            <OpSummaryComponent
                // TODO: use the SummaryView component
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                elements={props.opAutoRefraction}
                heading={rxKeyToHeadingMap['opAutoRefraction']}
                icon={rxKeyToIconMap['opAutoRefraction']}
            />
        );
    },
    opFinalPrescription: (props: { opIop?: PadElements['opFinalPrescription'] }) => {
        return (
            <OpSummaryComponent
                // TODO: use the SummaryView component
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                elements={props.opFinalPrescription}
                heading={rxKeyToHeadingMap['opFinalPrescription']}
                icon={rxKeyToIconMap['opFinalPrescription']}
            />
        );
    },
    careCanvas: (props: { careCanvas?: PadElements['careCanvas'] }) => {
        return <CareCanvasComponent {...props} />;
    },
};

type VisitWithLocalMassaging = Visit & {
    localInCompletePxId?: string[];
};

function getPropObj(item: GeniePadElementsSettingItem, propObj: PadElements) {
    const arg: PadElements = {},
        currId = item.id;
    // eslint-disable-next-line
    // @ts-ignore
    arg[currId as keyof PadElementsRXPReview] = propObj[currId as keyof PadElementsRXPReview];
    return arg;
}

const RXPreviewPtDetails: React.FC<{
    prescription: Prescription | undefined;
    visit: VisitWithLocalMassaging | undefined;
}> = (props) => {
    const padElementsConfig: GeniePadElementsSettingItem[] = useSelector(getPxElementConfig);
    const sectionNameConfig = useSelector(getSectionNameConfig);

    if (props.visit && !props?.visit?.prescriptionUrl) {
        if (
            props?.visit.status === 'CMNP' ||
            props?.visit.status === 'AB' ||
            props?.visit.status === 'NS'
        ) {
            return <NoPx localInCompletePxId={props?.visit?.localInCompletePxId} />;
        }
    }

    if (!props.prescription) {
        if (props.visit?.status === 'BK') {
            return <NoPx localInCompletePxId={props?.visit?.localInCompletePxId} />;
        }
        return null;
    }

    const { prescription } = props;

    const propObj: PadElements = {
        vitals: prescription.vitals,
        medicalHistory: prescription.patientHistory,
        symptoms: prescription.symptoms,
        diagnosis: prescription.diagnosis,
        medications: prescription.medications,
        injections: prescription.injections,
        labTests: prescription.labTests,
        labVitals: prescription.labVitals,
        examinations: prescription.examinations,
        prescriptionNotes: prescription.prescriptionNotes,
        refer: prescription.refer,
        advices: prescription.advices,
        followup: prescription.followup,
        dyform: prescription?.dyform,
        dentalExaminations: prescription?.dentalExaminations,
        dentalProcedures: prescription?.dentalProcedures,
        opVision: prescription?.opVision,
        opIop: prescription?.opIop,
        opCurrentSpec: prescription?.opCurrentSpec,
        opSubjectiveRefraction: prescription?.opSubjectiveRefraction,
        opAutoRefraction: prescription?.opAutoRefraction,
        opFinalPrescription: prescription?.opFinalPrescription,
        procedures: prescription?.procedures,
        careCanvas: prescription.careCanvas,
    };

    if (isPrescriptionEmpty(propObj)) {
        return <SummaryEmptyView />;
    }

    const remainingPxElements = Object.keys(PAD_COMPONENTS).filter((elem) => {
        if (!padElementsConfig?.find((e) => e.id === elem)) {
            return true;
        }
        return false;
    });

    const renderPadElements: (item: GeniePadElementsSettingItem) => React.ReactNode = (
        item: GeniePadElementsSettingItem,
    ) => {
        if (PAD_COMPONENTS[item.id as keyof PadElements]) {
            return PAD_COMPONENTS[item.id as keyof PadElements]?.({
                ...getPropObj(item, propObj),
                sectionName: getSectionNameFromKey(item.id, sectionNameConfig),
            });
        } else {
            const section = propObj.dyform && propObj.dyform.find((val) => val.id === item.id);
            return <DynamicFormComponent section={section} />;
        }
    };

    const renderOtherElements = (item: string) => {
        return PAD_COMPONENTS[item as keyof PadElements]?.({
            ...getPropObj({ id: item } as GeniePadElementsSettingItem, propObj),
            sectionName: getSectionNameFromKey(item, sectionNameConfig),
        });
    };

    return (
        <ViewSummary
            padElementsConfig={padElementsConfig}
            remainingPxElements={remainingPxElements}
            renderPadElements={renderPadElements}
            renderOtherElements={renderOtherElements}
        />
    );
};

export default RXPreviewPtDetails;
